import * as React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Download, Loader2, CheckCircle2, XCircle, FileSpreadsheet } from 'lucide-react';
import { getExportDownloadUrl } from '@/api/admin-exports';
import type { Export } from '@/types/admin-export';
import { tokenStorage } from '@/lib/token';

interface ExportProgressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportData: Export | null;
  isLoading: boolean;
  error: Error | null;
  onReset: () => void;
}

export function ExportProgressModal({
  open,
  onOpenChange,
  exportData,
  isLoading,
  error,
  onReset,
}: ExportProgressModalProps) {
  const handleClose = () => {
    onOpenChange(false);
    // Reset after animation completes
    setTimeout(onReset, 200);
  };

  const handleDownload = async () => {
    if (!exportData?.id) return;

    // Direct download from Azure/storage URL
    if (exportData.downloadUrl) {
      window.open(exportData.downloadUrl, '_blank');
      return;
    }

    // Fallback: authenticated download via API
    const url = getExportDownloadUrl(exportData.id);
    const token = tokenStorage.getAccessToken();

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = exportData.filePath || 'export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '';
    const units = ['o', 'Ko', 'Mo', 'Go'];
    let unitIndex = 0;
    let size = bytes;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const getStatusIcon = () => {
    if (error || exportData?.status === 'failed') {
      return <XCircle className="h-12 w-12 text-red-500" />;
    }
    if (exportData?.status === 'completed') {
      return <CheckCircle2 className="h-12 w-12 text-green-500" />;
    }
    if (isLoading || exportData?.status === 'processing') {
      return <Loader2 className="h-12 w-12 text-primary animate-spin" />;
    }
    return <FileSpreadsheet className="h-12 w-12 text-gray-400" />;
  };

  const getStatusText = () => {
    if (error) return 'Erreur';
    switch (exportData?.status) {
      case 'pending':
        return 'Preparation...';
      case 'processing':
        return 'Generation en cours...';
      case 'completed':
        return 'Export termine';
      case 'failed':
        return 'Echec de l\'export';
      default:
        return 'Initialisation...';
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Export Excel</AlertDialogTitle>
          <AlertDialogDescription>
            {exportData?.totalItems
              ? `${exportData.totalItems} elements a exporter`
              : 'Calcul du nombre d\'elements...'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col items-center gap-4 py-6">
          {getStatusIcon()}
          <p className="text-sm font-medium text-gray-700">{getStatusText()}</p>

          {/* Progress bar */}
          {(exportData?.status === 'processing' || isLoading) && (
            <div className="w-full space-y-2">
              <div className="flex items-center gap-3">
                <Progress value={exportData?.progress ?? 0} className="h-3 flex-1" />
                <span className="text-xs text-gray-500 tabular-nums">{exportData?.progress ?? 0}%</span>
              </div>
              <p className="text-xs text-gray-500">
                {exportData?.processedItems ?? 0} / {exportData?.totalItems ?? '?'}
              </p>
            </div>
          )}

          {/* Error message */}
          {(error || exportData?.status === 'failed') && (
            <p className="text-sm text-red-600 text-center">
              {error?.message || exportData?.errorMessage || 'Une erreur est survenue'}
            </p>
          )}

          {/* Completed info */}
          {exportData?.status === 'completed' && (
            <div className="text-center space-y-1">
              <p className="text-sm text-gray-600">
                Fichier: {exportData.filePath}
              </p>
              {exportData.fileSize && (
                <p className="text-xs text-gray-500">
                  Taille: {formatFileSize(exportData.fileSize)}
                </p>
              )}
            </div>
          )}
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {exportData?.status === 'completed' ? (
            <>
              <AlertDialogCancel onClick={handleClose}>Fermer</AlertDialogCancel>
              <Button onClick={handleDownload}>
                <Download className="mr-2 h-4 w-4" />
                Telecharger
              </Button>
            </>
          ) : (
            <AlertDialogCancel onClick={handleClose} disabled={isLoading}>
              {isLoading ? 'Annuler' : 'Fermer'}
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
